import { useState, useEffect, useCallback, useRef } from 'react';

declare global {
  interface Window {
    fbAsyncInit: () => void;
    FB: any;
  }
}

interface FacebookLoginProps {
  appId: string;
  onSuccess: (response: any) => void;
  label?: string;
  className?: string;
}

/**
 * Injects the Facebook SDK script and initialises it.
 * Resolves once `window.FB` is ready.
 *
 * IMPORTANT: This must be called BEFORE the user clicks the login button,
 * because FB.login() must run synchronously inside a click handler to avoid
 * popup-blockers.
 */
function loadFacebookSdk(appId: string): Promise<void> {
  if (window.FB) return Promise.resolve();

  return new Promise<void>((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB.init({
        appId,
        cookie: false,
        xfbml: false,
        version: 'v21.0',
      });
      resolve();
    };

    const existing = document.getElementById('facebook-jssdk') as HTMLScriptElement | null;
    if (existing) {
      if (window.FB) {
        resolve();
        return;
      }
      // Script tag exists but hasn't finished — wait for fbAsyncInit (set above).
      existing.addEventListener('error', () => reject(new Error('Failed to load Facebook SDK')), { once: true });
      return;
    }

    const script = document.createElement('script');
    script.id = 'facebook-jssdk';
    script.src = 'https://connect.facebook.net/pt_PT/sdk.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onerror = () => reject(new Error('Failed to load Facebook SDK'));
    document.body.appendChild(script);

    setTimeout(() => {
      if (!window.FB) {
        reject(new Error('Facebook SDK timed out'));
      }
    }, 15_000);
  });
}

const FacebookLoginButton = ({ appId, onSuccess, label = "Continuar com Facebook", className = "" }: FacebookLoginProps) => {
  const [, setSdkReady] = useState(!!window.FB);
  const [sdkError, setSdkError] = useState<string | null>(null);
  const [loginPending, setLoginPending] = useState(false);
  const onSuccessRef = useRef(onSuccess);
  onSuccessRef.current = onSuccess;

  // Pre-load the SDK on mount so it's ready when the user clicks
  useEffect(() => {
    if (window.FB) {
      setSdkReady(true);
      return;
    }

    loadFacebookSdk(appId)
      .then(() => setSdkReady(true))
      .catch((err) => {
        console.warn('Facebook SDK failed to pre-load:', err);
        // Don't show error yet — user hasn't clicked; SDK will retry on click
      });
  }, [appId]);

  const handleLogin = useCallback(() => {
    setSdkError(null);

    // If SDK isn't loaded yet, try loading it and open login after
    if (!window.FB) {
      setLoginPending(true);
      loadFacebookSdk(appId)
        .then(() => {
          setSdkReady(true);
          setLoginPending(false);
          // Note: this call is async so the popup may still be blocked.
          // We show a fallback message if it happens.
          try {
            window.FB.login((response: any) => {
              if (response.authResponse) {
                window.FB.api('/me', { fields: 'name,email,picture' }, (profile: any) => {
                  onSuccessRef.current({ ...response, profile });
                });
              }
            }, { scope: 'public_profile,email' });
          } catch {
            setSdkError('O popup foi bloqueado. Permita pop-ups para este site e tente novamente.');
          }
        })
        .catch((err) => {
          setSdkError(err instanceof Error ? err.message : 'Não foi possível carregar o Facebook.');
          setLoginPending(false);
        });
      return;
    }

    // SDK is ready — call FB.login SYNCHRONOUSLY within the click handler
    // so the browser doesn't block the popup.
    window.FB.login((response: any) => {
      if (response.authResponse) {
        window.FB.api('/me', { fields: 'name,email,picture' }, (profile: any) => {
          onSuccessRef.current({ ...response, profile });
        });
      }
    }, { scope: 'public_profile,email' });
  }, [appId]);

  return (
    <>
      <button
        onClick={handleLogin}
        disabled={loginPending}
        className={[
          "w-full inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3",
          "text-[0.95rem] font-semibold",
          "border border-[#334155] bg-[#0f172a] text-[#f1f5f9]",
          "hover:bg-white/5 transition-colors",
          "disabled:opacity-50 disabled:cursor-not-allowed",
          className,
        ].join(" ")}
      >
        {/* Facebook icon */}
        <svg className="w-5 h-5 text-[#1877F2]" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M24 12.073c0-6.627-5.373-12-12-12S0 5.446 0 12.073c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
        </svg>
        {loginPending ? 'A carregar...' : label}
      </button>
      {sdkError && <p className="mt-2 text-xs text-red-400">{sdkError}</p>}
    </>
  );
};

export default FacebookLoginButton;